import { Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DwComponent,
  DwComponentType,
  DwEventService,
  DwItemAttribute,
  DwMetaDataGridActionState,
  DwMetaDataRoutingService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwRoutingService,
  DwSecurityUserService
} from '@devwareapps/devware-cap';

import {
  AppMetaDataItemNames,
  AssignmentStudentGridQueryEntity
} from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { AssignmentStudentDetailActionService } from '../../actions/assignment-student-detail-action.service';
import { gridAnnimations } from '../manage-users-grid-renderer/grid-animations.const';
import { AssignmentGridFilterAttributes, AssignmentGridRendererBaseComponent } from '../assignments-grid-renderer/assignment-grid-renderer-base.component';

interface AssignmentStudentGridRowState {
  readonly progressPercent: number;
  readonly progressClass: string;
  readonly completedDateLabel: string;
  readonly onTimeLabel: string;
  readonly onTimeClass: string;
  readonly acknowledgedLabel: string;
  readonly acknowledgedDateLabel: string;
  readonly acknowledgedClass: string;
}

@DwComponent({
  key: 'assignment-student-grid-renderer',
  name: 'Assignment Student Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.AssignmentStudent,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-assignment-student-grid-renderer',
  templateUrl: './assignment-student-grid-renderer.component.html',
  styleUrls: ['./assignment-student-grid-renderer.component.scss'],
  animations: gridAnnimations
})
export class AssignmentStudentGridRendererComponent
  extends AssignmentGridRendererBaseComponent<AssignmentStudentGridQueryEntity> {

  public sortColumns = {
    studentName: '',
    progress: '',
    completed: ''
  };

  public sortColumn: string = '';
  public selectedAssignmentStudentId: number | null = null;
  public rowStateById: { [id: number]: AssignmentStudentGridRowState } = {};

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    dwMetaDataRoutingService: DwMetaDataRoutingService,
    router: Router,
    activatedRoute: ActivatedRoute,
    dwSecurityUserService: DwSecurityUserService,
    private assignmentStudentDetailActionService: AssignmentStudentDetailActionService
  ) {
    super(
      dwRoutingService,
      adminManagerService,
      dwMetaDataService,
      dwEventService,
      dwMetaDataRoutingService,
      router,
      activatedRoute,
      dwSecurityUserService
    );
  }

  setupRenderer(): void {
    this.resolveSortColumns();
    super.setupRenderer();
  }

  protected onGridConfigUpdated(): void {
    this.resolveSortColumns();
    super.onGridConfigUpdated();
  }

  public selectAssignmentStudent(row: AssignmentStudentGridQueryEntity): void {
    this.selectedAssignmentStudentId = row.AssignmentStudentId || null;
    this.selectedItems = [row];
  }

  public viewAssignmentStudentDetails(row: AssignmentStudentGridQueryEntity): void {
    if (!row?.AssignmentStudentId) {
      return;
    }

    this.selectAssignmentStudent(row);
    this.assignmentStudentDetailActionService.handleAction({
      data: row
    } as DwMetaDataGridActionState<any>).subscribe();
  }

  public trackByAssignmentStudent(index: number, item: AssignmentStudentGridQueryEntity): string | number {
    return item.AssignmentStudentId || item.StudentId || index;
  }

  protected mapResults(results: any[]): AssignmentStudentGridQueryEntity[] {
    const rows = (results || []) as AssignmentStudentGridQueryEntity[];
    const nextState = this.requestedPageInfo.pageNo === 1 ? {} : { ...this.rowStateById };

    rows.forEach(row => {
      nextState[this.getRowId(row)] = this.buildRowState(row);
    });
    this.rowStateById = nextState;

    return rows;
  }

  protected getTextSearchAttributes(): (string | DwItemAttribute)[] {
    return [];
  }

  protected getFilterAttributes(): AssignmentGridFilterAttributes {
    return {};
  }

  protected getDefaultSortDirection(column: string): 'asc' | 'desc' {
    return column && column === this.sortColumns.studentName ? 'asc' : super.getDefaultSortDirection(column);
  }

  private buildRowState(row: AssignmentStudentGridQueryEntity): AssignmentStudentGridRowState {
    const progressPercent = this.normalizePercent(row.PercentComplete);
    const acknowledged = !!row.Acknowledged || !!row.AcknowledgedDateTime;
    const completedDateLabel = row.CompletedDateTime || '';
    const completedOnTime = row.RoAssignmentStudentCompletedDetailsCompletedOnTime;

    return {
      progressPercent,
      progressClass: progressPercent >= 100 ? 'assignment-student-grid__progress-fill--complete' : '',
      completedDateLabel,
      onTimeLabel: row.CompletedDateTime && completedOnTime != null ? completedOnTime ? 'On time' : 'Late' : '',
      onTimeClass: completedOnTime === false ? 'assignment-student-grid__pill--late' : 'assignment-student-grid__pill--on-time',
      acknowledgedLabel: acknowledged ? 'Acknowledged' : 'Not yet',
      acknowledgedDateLabel: row.AcknowledgedDateTime || '',
      acknowledgedClass: acknowledged ? 'assignment-student-grid__pill--acknowledged' : 'assignment-student-grid__pill--muted'
    };
  }

  private getRowId(row: AssignmentStudentGridQueryEntity): number {
    return row.AssignmentStudentId || row.StudentId || 0;
  }

  private normalizePercent(percentComplete?: number): number {
    return Math.max(0, Math.min(100, Math.round(percentComplete || 0)));
  }

  private resolveSortColumns(): void {
    this.sortColumns = {
      studentName: this.findSortField([
        'StudentIdDisplay',
        'AssignmentStudent.StudentIdDisplay'
      ]),
      progress: this.findSortField([
        'PercentComplete',
        'AssignmentStudent.PercentComplete'
      ]),
      completed: this.findSortField([
        'CompletedDateTime',
        'AssignmentStudent.CompletedDateTime'
      ])
    };

    if (!this.sortColumn || !this.isKnownSortColumn(this.sortColumn)) {
      this.sortColumn = this.sortColumns.studentName || '';
    }
  }

  private findSortField(candidateFields: string[]): string {
    const columns = this.gridConfig?.columnDefs || [];

    for (const candidateField of candidateFields) {
      const matchingColumn = columns.find(column =>
        column?.field === candidateField
        || column?.key === candidateField
        || column?.fullSortField === candidateField);

      if (matchingColumn) {
        return matchingColumn.fullSortField || matchingColumn.field || matchingColumn.key || '';
      }
    }

    return '';
  }

  private isKnownSortColumn(fieldName: string): boolean {
    return !!fieldName && Object.keys(this.sortColumns)
      .map(key => this.sortColumns[key])
      .filter(value => !!value)
      .includes(fieldName);
  }
}
